import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useRef, useState } from "react"
import type { Resolver } from "react-hook-form"
import { FaPlus } from "react-icons/fa"

import { useCreateClassTemplate } from "@/hooks/api/useClassTemplates"
import { usePermissions } from "@/hooks/usePermissions"
import { class_templateCreateSchema } from "@/types/class_template"
import type {
  AddClassTemplateProps,
  ClassTemplateCreateInput,
  ClassTemplateUpdateInput,
} from "@/types/class_template"
import { Button } from "@chakra-ui/react"

import ClassTemplateFormDialog from "./ClassTemplateFormDialog"

const AddClassTemplate = memo(
  ({ onSuccess, onCancel }: AddClassTemplateProps = {}) => {
    const [isOpen, setIsOpen] = useState(false)
    const { hasPermission } = usePermissions()
    const createClassTemplateMutation = useCreateClassTemplate()
    const addButtonRef = useRef<HTMLButtonElement | null>(null)

    if (!hasPermission("class_templates:create")) {
      return null
    }

    const defaultValues = useMemo<ClassTemplateUpdateInput>(
      () => ({
        name: "",
        slug: "",
        description: "",
        program_type: "Group Lesson",
        session_id: "",
        instructor_id: "",
        swim_level: "Bubble",
        location: "Mountain View",
        day_of_week: "Monday",
        start_time: "",
        end_time: "",
        duration_minutes: 30,
        max_students: 3,
        min_students: 1,
        current_enrollment: 0,
        waitlist_enabled: true,
        price: undefined,
        prerequisites: "",
        notes: "",
        is_active: true,
        is_published: false,
      }),
      [],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: ClassTemplateUpdateInput) => {
      // Cast to CreateInput since all required fields will be present after form validation
      await createClassTemplateMutation.mutateAsync(data as ClassTemplateCreateInput)
      onSuccess?.()
    }

    return (
      <>
        <Button
          ref={addButtonRef}
          onClick={() => setIsOpen(true)}
          colorPalette="blue"
          size="sm"
          aria-label="Add new class"
        >
          <FaPlus fontSize="14px" aria-hidden="true" />
          Add Class
        </Button>

        <ClassTemplateFormDialog
          isOpen={isOpen}
          title="Add New Class"
          submitLabel="Create Class"
          /* TODO: zodResolver generic mismatch - CreateSchema infers CreateInput but dialog expects Resolver<UpdateInput> */
          resolver={zodResolver(class_templateCreateSchema) as Resolver<ClassTemplateUpdateInput>}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={createClassTemplateMutation.isPending}
          finalFocusRef={addButtonRef}
          showSkipLink
          loadingText="Creating..."
        />
      </>
    )
  },
)

AddClassTemplate.displayName = "AddClassTemplate"

export default AddClassTemplate
export type { AddClassTemplateProps }
